import {
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Switch,
    Typography,
} from "@mui/material"
import React from "react"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { GDPRUserDeletionPeriod } from "../gdprTypes"
import { createEnumChecker, iterateStringEnum } from "../../../../../../lib/enums"
import { makeStyles } from "tss-react/mui"
import { isEmpty } from "lodash"
import { SelectUserType } from "./SelectUserType"

const useStyles = makeStyles()(() => ({
    selectInput: {
        width: "18.75rem",
    },
    switchSelect: {
        width: "fit-content",
    },
}))

type UsersByDomainExportProps = Readonly<{
    userTypesFilter: string[]
    userDeletionPeriodFilter: GDPRUserDeletionPeriod
    includeDeletedFilter: boolean
    setUserTypesFilter: React.Dispatch<React.SetStateAction<string[]>>
    setIncludeDeletedFilter: (value: boolean) => void
    setUserDeletionPeriodFilter: (e: GDPRUserDeletionPeriod) => void
    exportedDomains?: string[]
}>

const isUserDeletionPeriodFilter = createEnumChecker(GDPRUserDeletionPeriod)

export function UsersByDomainExport({
    userTypesFilter,
    userDeletionPeriodFilter,
    includeDeletedFilter,
    setUserTypesFilter,
    setIncludeDeletedFilter,
    setUserDeletionPeriodFilter,
    exportedDomains,
}: UsersByDomainExportProps) {
    const { classes } = useStyles()

    function handleUsersDeletionPeriodFilter(e: SelectChangeEvent<GDPRUserDeletionPeriod>) {
        const value = e.target.value
        if (isUserDeletionPeriodFilter(value)) {
            setUserDeletionPeriodFilter(value)
        }
    }

    return (
        <>
            <Grid container gap="1rem" direction="column">
                <SelectUserType userTypes={userTypesFilter} setUserTypes={setUserTypesFilter} />
                <LocalizedStrict id="supplier-account-gdpr-export-include-deleted-switch-label" attrs={{ label: true }}>
                    <FormControlLabel
                        className={classes.switchSelect}
                        control={
                            <Switch
                                checked={includeDeletedFilter}
                                onClick={() => setIncludeDeletedFilter(!includeDeletedFilter)}
                            />
                        }
                        label="Include deleted users"
                    />
                </LocalizedStrict>
                {includeDeletedFilter && (
                    <FormControl className={classes.selectInput}>
                        <LocalizedStrict id="supplier-account-gdpr-export-user-deletion-period-select-input-label">
                            <InputLabel id="user-deletion-period-label">User deletion period</InputLabel>
                        </LocalizedStrict>
                        <LocalizedStrict
                            id="supplier-account-gdpr-export-user-deletion-period-select-label"
                            attrs={{ label: true }}
                        >
                            <Select
                                labelId="user-deletion-period-label"
                                id="user-deletion-period-select"
                                value={userDeletionPeriodFilter}
                                label="User deletion period"
                                onChange={handleUsersDeletionPeriodFilter}
                            >
                                {[...iterateStringEnum(GDPRUserDeletionPeriod)].map((value) => (
                                    <MenuItem key={value} value={value}>
                                        <LocalizedEnum
                                            base="supplier-account-gdpr-export-user-deletion-period-select-item"
                                            enumValue={value}
                                        >
                                            <span>{value}</span>
                                        </LocalizedEnum>
                                    </MenuItem>
                                ))}
                            </Select>
                        </LocalizedStrict>
                    </FormControl>
                )}
            </Grid>
            <Grid container gap="1rem" direction="column">
                <LocalizedStrict id="supplier-account-gdpr-export-exported-domains-subtitle">
                    <Typography variant="subtitle2">Verified domains being exported:</Typography>
                </LocalizedStrict>
                {exportedDomains && !isEmpty(exportedDomains) ? (
                    <Typography variant="body2">{exportedDomains.join(", ")}</Typography>
                ) : (
                    <LocalizedStrict id="supplier-account-gdpr-export-no-domains-registered">
                        <Typography variant="body2">No verified company domains have been registered.</Typography>
                    </LocalizedStrict>
                )}
            </Grid>
        </>
    )
}
